import React, { useRef, useEffect, useState } from 'react';
import {
  Chart,
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler,
} from 'chart.js';
import PropTypes from 'prop-types';
import ChartTooltip from './ChartTooltip';
import {colors} from '../../utils/colors.js';
import {getCSSVariable} from '../../utils/getCSSVariable.js';

Chart.register(
  LineController,
  LineElement,
  PointElement,
  CategoryScale,
  LinearScale,
  Tooltip,
  Legend,
  Filler
);

const isPlainObject = (val) =>
  val !== null && typeof val === 'object' && val.constructor === Object;

const deepMerge = (target = {}, source = {}) => {
  const out = { ...target };
  Object.keys(source).forEach((key) => {
    const srcVal = source[key];
    const tgtVal = target[key];

    if (isPlainObject(srcVal) && isPlainObject(tgtVal)) {
      out[key] = deepMerge(tgtVal, srcVal);
    } else {
      out[key] = srcVal;
    }
  });
  return out;
};

const LineChart = ({
  labels,
  datasets,
  width = '100%',
  height = 300,
  options = {},
  maintainAspectRatio = false,
  showLegend = false,
  cleanGrid = true,
  fillArea = false,
  pointRadius = 4,
  pointHoverRadius = 6,
  lineTension = 0,
}) => {
  const chartRef = useRef(null);
  const chartInstance = useRef(null);
  const [tooltip, setTooltip] = useState({
    visible: false,
    x: 0,
    y: 0,
    label: '',
    values: [],
  });

  // Track mouse position over the canvas
  useEffect(() => {
    const canvas = chartRef.current;
    if (!canvas) return;
    const handleMouseMove = (e) => {
      if (!tooltip.visible) return;
      setTooltip((prev) => ({
        ...prev,
        x: e.clientX, // Use viewport coordinates for fixed positioning
        y: e.clientY,
      }));
    };
    canvas.addEventListener('mousemove', handleMouseMove);
    return () => {
      canvas.removeEventListener('mousemove', handleMouseMove);
    };
  }, [tooltip.visible]);

  useEffect(() => {
    if (chartInstance.current) {
      chartInstance.current.destroy();
    }
    const ctx = chartRef.current.getContext('2d');

    const defaultOptions = {
      maintainAspectRatio,
      responsive: true,
      plugins: {
        legend: {
          display: showLegend,
          position: 'top',
          labels: {
            usePointStyle: true,
            padding: 20,
            font: {
              size: 12,
            },
          },
        },
        tooltip: {
          enabled: false,
          external: (context) => {
            const tooltipModel = context.tooltip;
            if (tooltipModel.opacity === 0) {
              setTooltip((prev) => ({ ...prev, visible: false }));
              return;
            }
            if (tooltipModel.dataPoints && tooltipModel.dataPoints.length > 0) {
              const dataIndex = tooltipModel.dataPoints[0].dataIndex;
              const label = tooltipModel.dataPoints[0].label;
              
              // Get all datasets and their values for this data index
              const chart = context.chart;
              const values = chart.data.datasets.map(dataset => ({
                label: dataset.label,
                value: dataset.data[dataIndex],
                color: dataset.borderColor || dataset.backgroundColor,
              }));
              
              setTooltip((prev) => ({
                ...prev,
                visible: true,
                label: label,
                values: values,
              }));
            }
          },
        },
      },
      scales: {
        x: {
          grid: { display: false },
          border: { display: false },
          ticks: {
            color: getCSSVariable('--bg-dark'),
            font: { size: 12 },
          },
        },
        y: {
          grid: cleanGrid
            ? {
              display: true,
              drawBorder: false,
              color: getCSSVariable('--bg-dark'),
              lineWidth: 1,
            }
            : { display: true },
          border: { display: false },
          ticks: {
            color: getCSSVariable('--bg-dark'),
            font: { size: 12 },
          },
        },
      },
      hover: {
        mode: 'nearest',
        intersect: true,
      },
      elements: {
        line: {
          tension: lineTension,
        },
        point: {
          radius: pointRadius,
          hoverRadius: pointHoverRadius,
        },
      },
      layout: {
        padding: {
          top: 10,
          bottom: 10,
        },
      },
    };

    // Add fill if requested
    const processedDatasets = datasets.map((dataset) => ({
      ...dataset,
      fill: fillArea ? 'origin' : false,
    }));

    // Deep merge options
    const mergedOptions = deepMerge(defaultOptions, options || {});

    chartInstance.current = new Chart(ctx, {
      type: 'line',
      data: {
        labels,
        datasets: processedDatasets,
      },
      options: mergedOptions,
    });

    return () => {
      chartInstance.current && chartInstance.current.destroy();
    };
  }, [
    labels,
    datasets,
    options,
    maintainAspectRatio,
    showLegend,
    cleanGrid,
    fillArea,
    pointRadius,
    pointHoverRadius,
    lineTension,
  ]);

  return (
    <div style={{ position: 'relative', width, height }}>
      <canvas ref={chartRef} />
      <ChartTooltip
        visible={tooltip.visible}
        x={tooltip.x}
        y={tooltip.y}
        content={
          <div style={{ minWidth: 120 }}>
            <div
              style={{
                color: 'var(--grey-64)',
                marginBottom: 8,
                fontWeight: 500,
                fontSize: '12px',
              }}
            >
              {tooltip.label}
            </div>
            {tooltip.values.map((item, index) => (
              <div
                key={index}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  marginBottom: 4,
                  fontSize: '12px',
                }}
              >
                <span
                  style={{
                    display: 'inline-block',
                    width: 14,
                    height: 14,
                    background: item.color,
                    borderRadius: 4,
                    marginRight: 8,
                  }}
                />
                <span
                  style={{
                    color: 'var(--grey-64)',
                    marginRight: 12,
                  }}
                >
                  {item.label}
                </span>
                <span
                  style={{color: colors.primaryColor, fontWeight: 'bold'}}
                >
                  {item.value}
                </span>
              </div>
            ))}
          </div>
        }
      />
    </div>
  );
};

LineChart.propTypes = {
  labels: PropTypes.arrayOf(PropTypes.string).isRequired,
  datasets: PropTypes.arrayOf(
    PropTypes.shape({
      label: PropTypes.string,
      data: PropTypes.arrayOf(PropTypes.number).isRequired,
      backgroundColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      borderWidth: PropTypes.number,
      pointBackgroundColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      pointBorderColor: PropTypes.oneOfType([
        PropTypes.string,
        PropTypes.arrayOf(PropTypes.string),
      ]),
      yAxisID: PropTypes.string,
    })
  ).isRequired,
  width: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  height: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.object,
  maintainAspectRatio: PropTypes.bool,
  showLegend: PropTypes.bool,
  cleanGrid: PropTypes.bool,
  fillArea: PropTypes.bool,
  pointRadius: PropTypes.number,
  pointHoverRadius: PropTypes.number,
  lineTension: PropTypes.number,
};

LineChart.defaultProps = {
  width: '100%',
  height: 300,
  options: {},
  maintainAspectRatio: false,
  showLegend: false,
  cleanGrid: true,
  fillArea: false,
  pointRadius: 4,
  pointHoverRadius: 6,
  lineTension: 0,
};

export default LineChart;
